import { Place, PlaceType } from "../Place";

// ─── Duración sugerida por tipo (minutos) ────────────────────────────────────

export const DURACION_POR_TIPO: Record<string, number> = {
  [PlaceType.Plaza]:           120,
  [PlaceType.ParqueTematico]:  180,
  [PlaceType.Campo]:           150,
  [PlaceType.JuegosNiños]:     120,
  [PlaceType.CamasElasticas]:   75,
  [PlaceType.ParqueAcuatico]:  240,
  [PlaceType.Museo]:            90,
  [PlaceType.Granja]:          120,
  [PlaceType.ParqueNatural]:   120,
  [PlaceType.EscapeRooms]:      75,
  [PlaceType.Restaurante]:      60,
  [PlaceType.Zoologicos]:      150,
  [PlaceType.Cine]:            120,
  [PlaceType.Arcade]:           90,
  [PlaceType.Shopping]:         90,
};

const VEL_AUTO_KMH = 25; // tráfico urbano BA

// ─── Tipos ───────────────────────────────────────────────────────────────────

export interface TramoInfo {
  distanciaKm:   number;
  tiempoAutoMin: number;
  tiempoAPieMin: number;
}

export interface ParadaPlan {
  place:                  Place;
  orden:                  number;
  duracionMin:            number;
  horaLlegada:            Date;
  horaSalida:             Date;
  tramoHastaSiguiente:    TramoInfo | null;
}

export interface PlanSalida {
  paradas:            ParadaPlan[];
  horaInicio:         Date;
  horaFinEstimada:    Date;
  distanciaTotalKm:   number;
}

// ─── Haversine ───────────────────────────────────────────────────────────────

export function haversineKm(
  lat1: number, lng1: number,
  lat2: number, lng2: number,
): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ─── Permutaciones (fuerza bruta, máx 4 elementos = 24 perms) ────────────────

function permutaciones<T>(arr: T[]): T[][] {
  if (arr.length <= 1) return [arr];
  return arr.flatMap((el, i) =>
    permutaciones([...arr.slice(0, i), ...arr.slice(i + 1)]).map(p => [el, ...p])
  );
}

export function optimizarRuta(places: Place[]): Place[] {
  if (places.length <= 1) return places;
  const perms = permutaciones(places);
  let mejorRuta = perms[0];
  let mejorDist = Infinity;
  for (const perm of perms) {
    let dist = 0;
    for (let i = 0; i < perm.length - 1; i++) {
      dist += haversineKm(perm[i].lat, perm[i].longi, perm[i + 1].lat, perm[i + 1].longi);
    }
    if (dist < mejorDist) {
      mejorDist = dist;
      mejorRuta = perm;
    }
  }
  return mejorRuta;
}

// ─── Construcción del plan ────────────────────────────────────────────────────

export function construirPlan(
  places: Place[],
  horaInicio: Date,
  duracionesCustom: Record<string, number>,
): PlanSalida {
  const rutaOptimizada = optimizarRuta(places);
  let cursor = new Date(horaInicio);
  const paradas: ParadaPlan[] = [];

  rutaOptimizada.forEach((place, i) => {
    const duracionMin =
      duracionesCustom[place.id] ??
      DURACION_POR_TIPO[place.placeType ?? PlaceType.JuegosNiños] ??
      90;

    const horaLlegada = new Date(cursor);
    const horaSalida  = new Date(cursor.getTime() + duracionMin * 60_000);

    let tramoHastaSiguiente: TramoInfo | null = null;

    if (i < rutaOptimizada.length - 1) {
      const next      = rutaOptimizada[i + 1];
      const distKm    = haversineKm(place.lat, place.longi, next.lat, next.longi);
      const autoMin   = Math.max(5, Math.round((distKm / VEL_AUTO_KMH) * 60));
      const apieMin   = Math.round((distKm / 5) * 60);
      tramoHastaSiguiente = {
        distanciaKm:   parseFloat(distKm.toFixed(2)),
        tiempoAutoMin: autoMin,
        tiempoAPieMin: apieMin,
      };
      cursor = new Date(horaSalida.getTime() + autoMin * 60_000);
    } else {
      cursor = horaSalida;
    }

    paradas.push({
      place,
      orden: i + 1,
      duracionMin,
      horaLlegada,
      horaSalida,
      tramoHastaSiguiente,
    });
  });

  const distanciaTotal = paradas.reduce(
    (acc, p) => acc + (p.tramoHastaSiguiente?.distanciaKm ?? 0), 0,
  );

  return {
    paradas,
    horaInicio,
    horaFinEstimada:  cursor,
    distanciaTotalKm: parseFloat(distanciaTotal.toFixed(2)),
  };
}

// ─── Helpers de formato ──────────────────────────────────────────────────────

export const fmtHora = (d: Date) =>
  d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });

export const fmtDuracion = (min: number) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
};

// ─── Sharing ─────────────────────────────────────────────────────────────────

export function buildGoogleMapsUrl(paradas: ParadaPlan[]): string {
  if (paradas.length === 0) return '';
  const origin      = `${paradas[0].place.lat},${paradas[0].place.longi}`;
  const destination = `${paradas[paradas.length - 1].place.lat},${paradas[paradas.length - 1].place.longi}`;
  const waypoints   = paradas
    .slice(1, -1)
    .map(p => `${p.place.lat},${p.place.longi}`)
    .join('|');
  const base = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
  return waypoints ? `${base}&waypoints=${waypoints}` : base;
}

export function buildWhatsAppText(plan: PlanSalida): string {
  const lines = plan.paradas.map(
    p => `${p.orden}. *${p.place.name}* — ${fmtHora(p.horaLlegada)} a ${fmtHora(p.horaSalida)}`,
  );
  const texto =
    `🗺️ *Plan de salida con chicos*\n\n` +
    lines.join('\n') +
    `\n\n⏱ Fin estimado: ${fmtHora(plan.horaFinEstimada)}` +
    `\n📍 Distancia total: ${plan.distanciaTotalKm} km` +
    `\n\n🌐 chicosxbaires.com.ar`;
  return encodeURIComponent(texto);
}
